import * as _ from 'lodash';
import moment from 'moment';
import CryptoJS from 'crypto-js';
import { ROLES } from '../constants';
import { SIGNUP_PROCESS_STEPS } from '../constants/index';
import { DATE_FORMATS } from './../constants/index';

type LocalStoragePayloadType<T> = {
  [K in keyof T]: string;
};

export const localStorageKeyFormatter = <T>(
  data: LocalStoragePayloadType<T>
) => {
  const keys = Object.keys(data);
  const keyValPair = keys.map((key) => {
    return { key, value: data[key] };
  });
  return keyValPair;
};
export const updateLocalStorage = (data) => {
  data.forEach((obj, index) => {
    localStorage.setItem(obj.key, JSON.stringify(obj.value));
  });
};
export const retrieveDataFromLocalStorage = (keys) => {
  if (keys.length === 1) {
    return localStorage.getItem(keys[0]);
  }
  const keyValues = keys.reduce((acc, key) => {
    const val = JSON.parse(localStorage.getItem(key));
    return { ...acc, [key]: val };
  }, {});
  return keyValues;
};

export const sortData = <T>(
  data: T[],
  field: string,
  order: 'asc' | 'desc' = 'asc'
): [] => {
  const plainData = data.map((obj) => ({ ...obj }));
  return _.orderBy(data, [field], [order]);
};

export const sliceData = (data, limit, offset = 0) => {
  const plainData = data.map((obj) => ({ ...obj }));
  return _.slice(plainData, offset * limit, offset * limit + limit);
};

export const isValidEmail = (email: string): boolean => {
  const pattern =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return pattern.test(email);
};

export function createResetAction(initialState) {
  return (state) => {
    Object.assign(state, initialState);
  };
}

export const isAdmin = (role) => {
  return role === ROLES.ADMIN;
};

export const isDoctor = (role) => {
  return role === ROLES.DENTIST;
};

export const isAssistant = (role) => {
  return role === ROLES.DOCTOR_ASSISTANT;
};

export const isTrialUser = (role) => {
  return role === ROLES.TRIAL_USER;
};

export const encryptData = (data) => {
  const secretKey = process.env.REACT_APP_ENCRYPTKEY;
  const encryptedData = CryptoJS.AES.encrypt(data, secretKey).toString();
  return encryptedData;
};

export const getSignupProcessFlags = (stepType, otp, otpToken = '') => {
  switch (stepType) {
    case SIGNUP_PROCESS_STEPS.VERIFY_NEW_USER:
      return {
        sendEmailOtp: false
      };
    case SIGNUP_PROCESS_STEPS.SEND_EMAIL_OTP:
      return {
        sendEmailOtp: true
      };
    case SIGNUP_PROCESS_STEPS.VERIFY_EMAIL_OTP:
      return {
        sendEmailOtp: false,
        emailOtp: otp,
        otpToken
      };
    case SIGNUP_PROCESS_STEPS.RESEND_EMAIL_OTP:
      return {
        sendEmailOtp: true
      };
    case SIGNUP_PROCESS_STEPS.VERIFY_MOBILE_OTP:
      return {
        sendEmailOtp: false,
        isEmailOptVerified: true,
        mobileOtp: otp,
        otpToken
      };

    case SIGNUP_PROCESS_STEPS.RESEND_MOBILE_OTP:
      return {
        sendEmailOtp: false,
        isEmailOptVerified: true,
        sendMobileOtp: true
      };
  }
};

export const maskedEmailUtil = (email) => {
  let maskedEmail = email;
  const atIndex = email.indexOf('@');
  if (atIndex > 4) {
    const visiblePart1 = email.slice(0, 4);
    const hiddenPart = 'X'.repeat(atIndex - 4);
    const visiblePart2 = email.slice(atIndex);
    maskedEmail = visiblePart1 + hiddenPart + visiblePart2;
  }
  return maskedEmail;
};

export const maskedEmailUtil2 = (email) => {
  if (!email || typeof email !== 'string') return '';

  const atIndex = email.indexOf('@');
  if (atIndex === -1) return email;

  const visiblePart1 = email.slice(0, 4);
  const hiddenCount = Math.min(atIndex - 4, 6);
  const hiddenPart = hiddenCount > 0 ? 'X'.repeat(hiddenCount) : '';
  const visiblePart2 = email.slice(atIndex);

  return visiblePart1 + hiddenPart + visiblePart2;
};

export const updateAllowedRoutes = (routes, rmvRoutes: string[]) =>
  _.filter(routes, (route) => !rmvRoutes.includes(route));

export const checkSubscriptionExpiry = (date) => {
  const expiredDate = new Date(date);
  const currentDate = new Date();
  return currentDate > expiredDate;
};

export const formatDate = ({ date, format }) => {
  return moment(date).format(format);
};

export function delay(millisec) {
  return new Promise((res) => {
    setTimeout(res, millisec);
  });
}

export const getCurrentYear = moment().year();

/**
 * Converts cents to dollars and rounds to two decimal places
 * @param {number} cents - Amount in cents
 * @returns {string} - Formatted dollar amount with 2 decimal places
 */
export const convertCentsToDollars = (cents: number) => {
  if (isNaN(cents)) {
    throw new Error('Input must be a valid number');
  }
  return (cents / 100).toFixed(2);
};

export const toSafeNumber = (value: unknown) => {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

// /**
//  * Converts milliseconds to a formatted date
//  * @param {number} milliseconds - Time value in milliseconds
//  * @returns {string} - formatted date
//  */
// export const convertMillisecondsToDate = (milliseconds: number) => {
//   if (isNaN(milliseconds)) {
//     throw new Error('Input must be a valid number');
//   }

//   // // since
//   const date = new Date(milliseconds * 1000);
//   const formatedDate = formatDate({ date, format: DATE_FORMATS.MONTH_FIRST });

//   return formatedDate;
// };

/**
 * Function to determine which values have changed between two objects
 * @param current - The current object
 * @param updated - The updated object
 * @returns An array of keys that have changed
 */
export const getChangedKeys = <T extends Record<string, any>>(
  current: T,
  updated: T
): (keyof T)[] =>
  Object.keys(current).filter(
    (key) => current[key as keyof T] !== updated[key as keyof T]
  ) as (keyof T)[];

/**
 * Sorts an array of data by a given field in ascending or descending order.
 *
 * @param data - Array of data to be sorted.
 * @param field - The field to sort the data by.
 * @param order - The order of sorting, either "asc" or "desc" (default: "asc").
 * @returns The sorted array.
 */
export const sortSearchedData = <T>(
  data: T[],
  field: string,
  order: 'asc' | 'desc' = 'asc'
): T[] => {
  // Clone the data to avoid mutating the original array
  const clonedData = [...data];

  return _.orderBy(
    clonedData,
    [
      (item) => {
        const value = _.get(item, field);

        // Handle "NA" values explicitly
        if (
          field !== 'writebackStatus' &&
          field !== 'PMSName' &&
          value === 'NA'
        ) {
          return order === 'asc' ? Infinity : -Infinity;
        }

        // Handle date strings
        if (typeof value === 'string' && isValidDate(value)) {
          return new Date(value).getTime();
        }

        // For other types (string, number, boolean), return the value directly
        return value;
      }
    ],
    [order]
  );
};

/**
 * Validates if a string is a valid date in "MM/DD/YYYY" or "MM/DD/YYYY hh:mm AM/PM" format.
 *
 * @param dateStr - The string to validate.
 * @returns True if the string is a valid date, false otherwise.
 */
const isValidDate = (dateStr: string): boolean => {
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
};

export const getDifferenceFromCurrentDate = (inputDate: string) => {
  if (!inputDate) {
    return {
      humanReadableFormat: 'None',
      diffInMilliseconds: 0
    };
  }
  const currentDate = new Date();
  const targetDate = new Date(inputDate);

  if (isNaN(targetDate.getTime())) {
    throw new Error('Invalid date format');
  }

  const diffInMilliseconds = Math.abs(
    currentDate.getTime() - targetDate.getTime()
  );

  const days = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (diffInMilliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor(
    (diffInMilliseconds % (1000 * 60 * 60)) / (1000 * 60)
  );
  const seconds = Math.floor((diffInMilliseconds % (1000 * 60)) / 1000);

  // Build the human-readable format dynamically
  const timeParts = [];
  if (days > 0) timeParts.push(`${days} days`);
  if (hours > 0) timeParts.push(`${hours} hours`);
  if (minutes > 0) timeParts.push(`${minutes} minutes`);
  if (seconds > 0) timeParts.push(`${seconds} seconds ago`);

  return {
    humanReadableFormat: timeParts.join(', '),
    diffInMilliseconds
  };
};
export const convertIntervalToMilliseconds = (value, unit) => {
  if (!unit || isNaN(value)) {
    return null;
  }

  switch (unit.toLowerCase()) {
    case 'minute':
    case 'minutes':
      return value * 60 * 1000;
    case 'hour':
    case 'hours':
      return value * 60 * 60 * 1000;
    case 'day':
    case 'days':
      return value * 24 * 60 * 60 * 1000;
    default:
      return null;
  }
};

export const maskPhoneNoUtil = (mobileNo) => {
  const digits = mobileNo?.length;
  return `XXXXXX${mobileNo?.slice(mobileNo.length - 4, digits)}`;
};
